import { useEffect, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";

/**
 * Poll the backend's latest JPEG frame.
 * Camera-first callers should pass cameraId. deviceId is retained only for
 * legacy screens that still use the old device endpoint.
 */
export function useDeviceFrame({ cameraId, deviceId, active, pollMs }) {
  const [src, setSrc] = useState(null);
  const [signal, setSignal] = useState("waiting");
  const [ratio, setRatio] = useState(null);
  const missesRef = useRef(0);
  const targetId = cameraId || deviceId;
  const endpoint = cameraId ? `cameras/${cameraId}/frame` : `devices/${deviceId}/frame`;

  useEffect(() => {
    if (!active || !targetId) {
      setSrc((old) => { if (old) URL.revokeObjectURL(old); return null; });
      setSignal("waiting");
      missesRef.current = 0;
      return undefined;
    }

    let cancelled = false;
    const measure = (blob) => {
      if (typeof createImageBitmap === "function") {
        createImageBitmap(blob).then((bmp) => {
          if (cancelled) { bmp.close?.(); return; }
          const r = bmp.width / bmp.height;
          setRatio((prev) => (prev && Math.abs(prev - r) < 0.02 ? prev : r));
          bmp.close?.();
        }).catch(() => {});
      } else {
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          const r = img.naturalWidth / img.naturalHeight;
          if (r > 0) setRatio((prev) => (prev && Math.abs(prev - r) < 0.02 ? prev : r));
        };
        img.src = URL.createObjectURL(blob);
      }
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${API_BASE}/${endpoint}?t=${Date.now()}`, { credentials: "include" });
        if (cancelled) return;
        if (resp.status === 200) {
          const blob = await resp.blob();
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          setSrc((old) => { if (old) URL.revokeObjectURL(old); return objectUrl; });
          setSignal("live");
          missesRef.current = 0;
          measure(blob);
        } else {
          missesRef.current += 1;
          if (missesRef.current > 6) setSignal("lost");
        }
      } catch {
        missesRef.current += 1;
        if (missesRef.current > 6) setSignal("lost");
      }
    };

    tick();
    const id = setInterval(tick, pollMs);
    return () => {
      cancelled = true;
      clearInterval(id);
      setSrc((old) => { if (old) URL.revokeObjectURL(old); return null; });
    };
  }, [active, endpoint, pollMs, targetId]);

  return { src, signal, ratio };
}
